import { Logo } from "@/components/layout/logo";
import { FpSidebarTrigger } from "@/components/ui/fp-sidebar";
import { listProjectsQueryOptions } from "@/lib/hooks/useProjects";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ProjectsGrid } from "../../components/projects/ProjectsGrid";
import { ProjectsHeader } from "../../components/projects/ProjectsHeader";

export const Route = createFileRoute("/(projects)/projects/")({
  component: ProjectsPage,
});

type SortOption = "modified" | "created" | "name";

function ProjectsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("modified");

  const { data, isLoading } = useQuery(listProjectsQueryOptions());
  const projects = data?.projects ?? [];

  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = projects.filter((project) => {
        const projectId = project.id.toLowerCase();
        const appName = project.appName?.toLowerCase() ?? "";
        const summary = project.summary?.toLowerCase() ?? "";
        return (
          projectId.includes(query) ||
          appName.includes(query) ||
          summary.includes(query)
        );
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name": {
          const nameA = a.appName || a.id;
          const nameB = b.appName || b.id;
          return nameA.localeCompare(nameB);
        }
        case "created":
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        default:
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
      }
    });

    return sorted;
  }, [projects, searchQuery, sortBy]);

  return (
    <div className="w-full h-full">
      <header className="border-b shrink-0">
        <div className="flex items-center justify-between h-12 px-4 min-h-12">
          <div className="flex items-center gap-2">
            <Logo />
            <FpSidebarTrigger />
          </div>
        </div>
      </header>
      <div className="container mx-auto px-6 py-8 grid gap-3">
        <ProjectsHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          projectCount={filteredAndSortedProjects.length}
          totalCount={projects.length}
        />

        <ProjectsGrid
          projects={filteredAndSortedProjects}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
